import {Buffer} from 'buffer';
import {initialize} from 'redux-form';
import {
  ChangePasswordRequestPayload,
  ECardInfoResponsePayload,
  GetHolderProfileCabinetResponsePayload,
  GetProfileSettingsResponsePayload,
  GetRequestResponsePayload,
  ProvidingListApplication,
  RequestedOperationType,
  RequestResponsePayload,
  UpdateProfileSettingsRequestPayload,
  UpdatingNotificationInfoRequestPayload,
  SendCodeFromCabinetRequestPayload,
  SendCodeResponsePayload,
} from '../../../shared/models/api';
import {profileSlice} from '../../reducers/profile';
import {httpService, HttpWrapperOptions} from '../../../shared/httpWrapper';
import {apiUrls} from '../../../shared/constants/urls';
import {getApiRoute, hashPassword, onlyDigitsPipe} from '../../../shared/pipes';
import {loginStepSlice} from '../../reducers/runtime/loginStep';
import {LoginState} from '../../../shared/models/login/loginStep';
import {checkLogin} from '../login';
import {Forms} from '../../../shared/models/forms';
import {NotificationResponsePayload} from '../../../shared/models/api';
import {NotificationStatus} from '../../../shared/models/profile/notification';
import {addNotification} from '../snackbar';
import {SnackbarNotificationType} from '../../../shared/components/snackbar/model';
import {storage, StorageKeys} from '../../../shared/storage';
import {getCabinetAuthHeaders} from '../../../shared/utils/cabinetAuthToken';
import {AppActions, AppThunk} from '..';
import {setBadgeCount} from '../../../shared/fcmWrapper/pushNotification';
import {CodeUsageFlag} from '../../../screens/profile/common/model';

export const getHolderProfile = (): AppThunk => {
  return async (dispatch) => {
    const route = getApiRoute(apiUrls.getProfile);
    const authHeaders = await getCabinetAuthHeaders();
    const options = new HttpWrapperOptions().TrackRequest().Headers(authHeaders);
    const holderProfile = await httpService.get(route, options);
    dispatch(profileSlice.actions.setHolderProfile(holderProfile));
  };
};

export const getProfileSettings = (): AppThunk => {
  return async (dispatch) => {
    const route = getApiRoute(apiUrls.getSettings);
    const options = new HttpWrapperOptions().TrackRequest();
    const profileSettings = await httpService.get(route, options);
    dispatch(profileSlice.actions.setProfileSettings(profileSettings));
  };
};

export const getProfilePhoto = (): AppThunk => {
  return async (dispatch) => {
    try {
      const options = new HttpWrapperOptions();
      options.responseType = 'arraybuffer';
      const profilePhoto = Buffer.from(
        await httpService.get<any>(getApiRoute(apiUrls.getPhoto), options),
        'binary',
      ).toString('base64');
      const photoObj = {
        photo: profilePhoto,
        thumbnail: profilePhoto, //ToDo thumbnail
      };
      dispatch(profileSlice.actions.setPhoto(photoObj));
    } catch (e) {
      console.error(e);
    }
  };
};

export const getProfileNotifications = (): AppThunk => {
  return async (dispatch) => {
    const route = getApiRoute(apiUrls.getNotifications);
    const options = new HttpWrapperOptions().TrackRequest();
    const profileNotifications = await httpService.get<NotificationResponsePayload[]>(route, options);
    dispatch(profileSlice.actions.setNotifications(profileNotifications));
    setBadgeCount(profileNotifications?.filter((n) => n.statusId === NotificationStatus.NotRead).length || 0);
  };
};

export const getProfileNotification = (notificationId: number): AppThunk => {
  return async (dispatch, getState) => {
    if (!notificationId) {
      return;
    }

    const profileNotification = getState().profile.notification;

    if (profileNotification?.id === notificationId) {
      return;
    }

    const newProfileNotification = await httpService.get<NotificationResponsePayload>(
      getApiRoute(apiUrls.getNotification, {notificationId}),
      new HttpWrapperOptions().Errors(false).ThrowException(false).Wait(false).IgnoreDisplayError(true),
    );

    dispatch(profileSlice.actions.setNotification(newProfileNotification));
  };
};

export const readNotification = (notificationId: number, httpOptions?: HttpWrapperOptions): AppThunk => {
  return async (dispatch, getState) => {
    const request = {
      notificationId,
      statusId: NotificationStatus.Read,
    } as UpdatingNotificationInfoRequestPayload;

    await httpService.put<void>(getApiRoute(apiUrls.readNotification), [request], httpOptions);
    dispatch(profileSlice.actions.updateNotificationStatus(request));

    const notReadNotifications = getState().profile.notifications?.filter(
      (n) => n.statusId === NotificationStatus.NotRead,
    );
    setBadgeCount(notReadNotifications?.length || 0);
  };
};

export const readRequest = (requestId: number): AppThunk => {
  return async (dispatch, getState) => {
    const request = getState().profile.requests?.find((r) => r.id === requestId);
    if (!request || request.isRead) {
      return;
    }

    await httpService.put(getApiRoute(apiUrls.changeRequestReadingStatus), [
      {
        requestId: request.id,
      },
    ]);
    dispatch(getProfileRequests());
  };
};

export const addOrUpdateNotification = (notification: NotificationResponsePayload): AppThunk => {
  return async (dispatch, getState) => {
    if (!notification) {
      return;
    }

    let notifications = getState().profile.notifications ?? [];
    let isExists = false;

    notifications = notifications.map((item) => {
      if (item.id === notification.id) {
        isExists = true;
        return notification;
      }
      return item;
    });

    if (!isExists) {
      notifications = [notification, ...notifications];
    }

    dispatch(profileSlice.actions.setNotifications(notifications));
    const notReadNotifications = notifications?.filter((n) => n.statusId === NotificationStatus.NotRead);
    setBadgeCount(notReadNotifications?.length || 0);
  };
};

export const getProfileRequestById = (id: number): AppThunk => {
  return async (dispatch) => {
    dispatch(profileSlice.actions.setCurrentRequest(null));
    // fix in new task(#235)
    const profileRequest = await httpService.get<GetRequestResponsePayload>(
      getApiRoute(apiUrls.getRequestById, {requestId: id}),
    );
    // const profileRequest = null;
    dispatch(profileSlice.actions.setCurrentRequest(profileRequest));
  };
};

export const getProfileRequests = (): AppThunk => {
  return async (dispatch) => {
    const route = getApiRoute(apiUrls.getRequests);
    const options = new HttpWrapperOptions().TrackRequest();
    const profileRequests = await httpService.get<RequestResponsePayload[]>(route, options);
    dispatch(profileSlice.actions.setRequests(profileRequests));
  };
};

export const updateProfileSettings = (changebleProfileSettings: UpdateProfileSettingsRequestPayload): AppThunk => {
  return async (dispatch, getState) => {
    const profileSettings = getState().profile.settings!;
    const newProfileSettings: GetProfileSettingsResponsePayload = {
      ...profileSettings,
      events: changebleProfileSettings.events,
      // email: changebleProfileSettings.email,
      // phoneNumber: changebleProfileSettings.phoneNumber,
      receiveInformationOffers: changebleProfileSettings.receiveInformationOffers,
      receiveInformationShares: changebleProfileSettings.receiveInformationShares,
      receiveSurveyInformation: changebleProfileSettings.receiveSurveyInformation,
    };

    await httpService.put(getApiRoute(apiUrls.setSettings), newProfileSettings, new HttpWrapperOptions().Wait(true));

    dispatch(profileSlice.actions.setProfileSettings(newProfileSettings));
  };
};

export const updateProfile =
  (newProfile: GetHolderProfileCabinetResponsePayload): AppThunk =>
  async (dispatch) => {
    dispatch(profileSlice.actions.setHolderProfile(newProfile));
  };

export const updateProfilePassword =
  (oldPassword: string, newPassword: string): AppThunk =>
  async (dispatch) => {
    const currentHash = hashPassword(oldPassword),
      newHash = hashPassword(newPassword);

    await httpService.post<void>(
      getApiRoute(apiUrls.changePassword),
      {currentHash, newHash} as ChangePasswordRequestPayload,
      new HttpWrapperOptions().Wait(true).Errors(false).ThrowException(true),
    );

    dispatch(
      addNotification({
        message: 'Пароль был успешно изменен',
        type: SnackbarNotificationType.Success,
      }),
    );
  };

export const requestContactChange =
  (newValue: string, type: RequestedOperationType): AppThunk =>
  async (dispatch) => {
    const results = await httpService.post<SendCodeResponsePayload>(
      getApiRoute(apiUrls.sendCodeFromCabinet),
      {
        requestedOperation: type,
        operationParameter: newValue,
      } as SendCodeFromCabinetRequestPayload,
      new HttpWrapperOptions().Wait(true).Errors(true).ThrowException(true),
    );
    dispatch(profileSlice.actions.setChangeContactsNewValue(newValue));
    dispatch(profileSlice.actions.setChangeContactsResponse(results));
    await storage.setItem(StorageKeys.changeContactsCodeId, results?.codeId);
    await storage.setItem<CodeUsageFlag>(StorageKeys.changeContactsCodeIdUsage, 'unUsed');
  };

export const confirmContactsChange =
  (code: string): AppThunk =>
  async (dispatch) => {
    await httpService.post<void>(
      getApiRoute(apiUrls.changeContacts),
      {
        codeId: (await storage.getItem(StorageKeys.changeContactsCodeId)) || 0,
        code,
      },
      new HttpWrapperOptions().Wait(true).Errors(true).ThrowException(true),
    );
    await dispatch(getHolderProfile());
    await storage.setItem<CodeUsageFlag>(StorageKeys.changeContactsCodeIdUsage, 'used');
  };

export const getProfileApplications = (): AppThunk => async (dispatch) => {
  try {
    const route = getApiRoute(apiUrls.getApplications);
    const options = new HttpWrapperOptions().TrackRequest();
    const results = await httpService.get<ProvidingListApplication[]>(route, options);
    dispatch(profileSlice.actions.setApplications(results));
  } catch (err) {
    console.error(err);
  }
};

export const endSession = (): AppThunk => async (dispatch, getState) => {
  const state = getState();
  await storage.removeItem(StorageKeys.token);
  if (state.profile.token && !state.profile.isSessionExpired) {
    await dispatch(loginStepSlice.actions.setLoginStep(LoginState.login));
    const identificator =
      state.profile.holder?.mobilePhone || state.profile.settings?.phoneNumber || state.profile.holder?.snils || null;
    if (identificator === null) {
      await dispatch({type: AppActions.Reset});
      await dispatch(profileSlice.actions.reset(null));
      return;
    }
    await dispatch(checkLogin(onlyDigitsPipe(identificator)) as any);
    await dispatch(initialize(Forms.loginForm, {identificator}));
    await dispatch(profileSlice.actions.setSessionState(true));
    await dispatch(profileSlice.actions.setToken(null));
  }
};

export const getECardInfo = (): AppThunk => {
  return async (dispatch) => {
    const eCardInfo = await httpService.get<ECardInfoResponsePayload>(getApiRoute(apiUrls.getECardInfo));

    dispatch(profileSlice.actions.setECardInfo(eCardInfo));
  };
};

export const blockCard = (): AppThunk => {
  return async (dispatch) => {
    try {
      await httpService.post<void>(
        getApiRoute(apiUrls.blockCard),
        {},
        new HttpWrapperOptions().Wait(true).Errors(true).ThrowException(true),
      );
      await dispatch(getHolderProfile());
    } catch (err) {}
  };
};
